import { useState } from 'react'
import { useCart } from '@chordcommerce/gatsby-theme-autonomy'
import { useOrderTags } from '~/hooks/components/use-order-tags'
import { getDoesCartHaveBulkItems } from '~/utils/bulk'
import { getDoesCartHaveClubItems } from '~/utils/subscriptions'

export function useEmptyCart() {
  const { removeTags, TAGS } = useOrderTags()

  const { cart, loadCart, modifyCart } = useCart()
  const { lineItems = [], state, tagList } = cart || {}

  const [isEmptying, setIsEmptying] = useState(false)

  // Removes all products and tags from the cart.
  const emptyCart = async (force = false) => {
    // The cart state is checked in case the user has made it to Stripe Checkout
    // and is looking at the site in another tab. Emptying the cart at that
    // point can result in an Uncaptured payment. `force` bypasses that check
    // and should only be called when the user is taking action (going back
    // through club signup, adding something else to the cart, etc.).
    if (isEmptying || !(force || state === 'cart')) return

    setIsEmptying(true)
    if (lineItems?.length) {
      await modifyCart({
        attributes: {
          lineItemsAttributes: lineItems.map((lineItem) => ({
            id: lineItem.id,
            quantity: 0,
          })),
        },
      })
    }
    await removeTags({ tags: Object.values(TAGS) })
    if (lineItems?.length || tagList?.length) await loadCart()
    setIsEmptying(false)
  }

  // Clears club products/tags from the cart. Useful when the customer has made
  // it to the signup review step - where the products/tags get added - but then
  // returns to the rest of the site.
  const emptyClubCart = async (force = false) =>
    getDoesCartHaveClubItems(lineItems) && emptyCart(force)

  // Clears bulk line items from the cart when non-bulk items are getting added
  // and vice versa. Avoids having smaller items land in a Bulk DTC order.
  const emptyBulkCart = async (isBulk, force = false) => {
    const hasBulkItems = getDoesCartHaveBulkItems(lineItems)

    return (
      ((hasBulkItems && !isBulk) || (!hasBulkItems && isBulk)) &&
      emptyCart(force)
    )
  }

  return {
    emptyBulkCart,
    emptyCart,
    emptyClubCart,
  }
}
